import { useState, useMemo } from "react"
import {DollarSign, ChevronDown, RotateCcw, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Info, Scale, Percent, Target} from 'lucide-react'

interface OperationType {
  name: string
  description: string
  defaults: {
    herdSize: number
    calvingRate: number
    avgSaleWeight: number
    marketPrice: number
    cullCowPercent: number
    cullCowPrice: number
    feedCost: number
    vetHealth: number
    breedingAI: number
    pastureLand: number
    labor: number
    mineralSupp: number
    equipOverhead: number
  }
}

const OPERATION_TYPES: OperationType[] = [
  {
    name: "Small Cow-Calf (25-50 head)",
    description: "Typical small family ranch operation",
    defaults: {
      herdSize: 40,
      calvingRate: 88,
      avgSaleWeight: 550,
      marketPrice: 2.85,
      cullCowPercent: 12,
      cullCowPrice: 0.82,
      feedCost: 650,
      vetHealth: 45,
      breedingAI: 35,
      pastureLand: 180,
      labor: 120,
      mineralSupp: 40,
      equipOverhead: 95,
    },
  },
  {
    name: "Medium Cow-Calf (50-150 head)",
    description: "Mid-size commercial operation",
    defaults: {
      herdSize: 100,
      calvingRate: 90,
      avgSaleWeight: 575,
      marketPrice: 2.85,
      cullCowPercent: 14,
      cullCowPrice: 0.85,
      feedCost: 600,
      vetHealth: 40,
      breedingAI: 30,
      pastureLand: 160,
      labor: 100,
      mineralSupp: 38,
      equipOverhead: 85,
    },
  },
  {
    name: "Large Cow-Calf (150+ head)",
    description: "Large-scale commercial ranch",
    defaults: {
      herdSize: 250,
      calvingRate: 92,
      avgSaleWeight: 600,
      marketPrice: 2.85,
      cullCowPercent: 15,
      cullCowPrice: 0.88,
      feedCost: 550,
      vetHealth: 35,
      breedingAI: 25,
      pastureLand: 140,
      labor: 80,
      mineralSupp: 35,
      equipOverhead: 75,
    },
  },
  {
    name: "Custom Operation",
    description: "Enter your own values",
    defaults: {
      herdSize: 50,
      calvingRate: 90,
      avgSaleWeight: 550,
      marketPrice: 2.85,
      cullCowPercent: 12,
      cullCowPrice: 0.85,
      feedCost: 600,
      vetHealth: 40,
      breedingAI: 30,
      pastureLand: 160,
      labor: 100,
      mineralSupp: 38,
      equipOverhead: 85,
    },
  },
]

function getProfitHealth(profitPerCow: number): { label: string; color: string; bgColor: string; icon: "check" | "warning" | "alert"; message: string } {
  if (profitPerCow >= 200) {
    return { label: "Excellent", color: "#2D6A4F", bgColor: "#B7E4C7", icon: "check", message: "Your operation is generating strong per-head profit. Consider reinvesting in herd genetics or infrastructure." }
  }
  if (profitPerCow >= 50) {
    return { label: "Healthy", color: "#40916C", bgColor: "#d1f0dd", icon: "check", message: "Solid profitability. Focus on maintaining low costs and optimizing calving rates to sustain margins." }
  }
  if (profitPerCow >= 0) {
    return { label: "Marginal", color: "#D4A373", bgColor: "#FEFAE0", icon: "warning", message: "Thin margins. Review your largest expense categories and consider ways to increase sale weight or market timing." }
  }
  if (profitPerCow >= -100) {
    return { label: "At Risk", color: "#E76F51", bgColor: "#fce8e2", icon: "alert", message: "Operating at a loss. Evaluate feed costs, cull rate, and whether pasture lease expenses can be reduced." }
  }
  return { label: "Critical Loss", color: "#c0392b", bgColor: "#fde2e2", icon: "alert", message: "Significant losses per head. Immediate cost restructuring or herd reduction may be necessary." }
}

export default function CowCalfProfitCalculator() {
  const [opType, setOpType] = useState(OPERATION_TYPES[1])
  const [herdSize, setHerdSize] = useState("100")
  const [calvingRate, setCalvingRate] = useState("90")
  const [avgSaleWeight, setAvgSaleWeight] = useState("575")
  const [marketPrice, setMarketPrice] = useState("2.85")
  const [cullCowPercent, setCullCowPercent] = useState("14")
  const [cullCowPrice, setCullCowPrice] = useState("0.85")
  const [feedCost, setFeedCost] = useState("600")
  const [vetHealth, setVetHealth] = useState("40")
  const [breedingAI, setBreedingAI] = useState("30")
  const [pastureLand, setPastureLand] = useState("160")
  const [labor, setLabor] = useState("100")
  const [mineralSupp, setMineralSupp] = useState("38")
  const [equipOverhead, setEquipOverhead] = useState("85")
  const [showResults, setShowResults] = useState(false)

  const applyPreset = (op: OperationType) => {
    setOpType(op)
    setHerdSize(String(op.defaults.herdSize))
    setCalvingRate(String(op.defaults.calvingRate))
    setAvgSaleWeight(String(op.defaults.avgSaleWeight))
    setMarketPrice(String(op.defaults.marketPrice))
    setCullCowPercent(String(op.defaults.cullCowPercent))
    setCullCowPrice(String(op.defaults.cullCowPrice))
    setFeedCost(String(op.defaults.feedCost))
    setVetHealth(String(op.defaults.vetHealth))
    setBreedingAI(String(op.defaults.breedingAI))
    setPastureLand(String(op.defaults.pastureLand))
    setLabor(String(op.defaults.labor))
    setMineralSupp(String(op.defaults.mineralSupp))
    setEquipOverhead(String(op.defaults.equipOverhead))
    setShowResults(false)
  }

  const results = useMemo(() => {
    const h = parseFloat(herdSize)
    const cr = parseFloat(calvingRate) / 100
    const sw = parseFloat(avgSaleWeight)
    const mp = parseFloat(marketPrice)
    const cc = parseFloat(cullCowPercent) / 100
    const cp = parseFloat(cullCowPrice)
    const fc = parseFloat(feedCost)
    const vh = parseFloat(vetHealth)
    const ba = parseFloat(breedingAI)
    const pl = parseFloat(pastureLand)
    const lb = parseFloat(labor)
    const ms = parseFloat(mineralSupp)
    const eo = parseFloat(equipOverhead)

    if ([h, cr, sw, mp, cc, cp, fc, vh, ba, pl, lb, ms, eo].some((v) => isNaN(v) || v < 0) || h <= 0) return null

    // Revenue
    const calvesWeaned = Math.floor(h * cr)
    const calfRevenue = calvesWeaned * sw * mp
    const cullCowCount = Math.floor(h * cc)
    const avgCullWeight = 1150 // average cull cow weight
    const cullRevenue = cullCowCount * avgCullWeight * cp
    const totalRevenue = calfRevenue + cullRevenue
    const revenuePerCow = totalRevenue / h

    // Expenses (per head * herd)
    const expensePerHead = fc + vh + ba + pl + lb + ms + eo
    const totalExpenses = expensePerHead * h

    // Profit
    const netProfit = totalRevenue - totalExpenses
    const profitPerCow = netProfit / h

    // Break-even price per lb of calf
    const breakEvenPrice = calvesWeaned > 0 ? (totalExpenses - cullRevenue) / (calvesWeaned * sw) : 0

    // ROI
    const roi = totalExpenses > 0 ? (netProfit / totalExpenses) * 100 : 0

    // Expense breakdown for chart
    const expenses = [
      { name: "Feed & Hay", value: fc, color: "#2D6A4F" },
      { name: "Pasture / Land", value: pl, color: "#40916C" },
      { name: "Labor", value: lb, color: "#606C38" },
      { name: "Equipment / Overhead", value: eo, color: "#D4A373" },
      { name: "Vet & Health", value: vh, color: "#E76F51" },
      { name: "Mineral / Supplement", value: ms, color: "#B7E4C7" },
      { name: "Breeding / AI", value: ba, color: "#283618" },
    ].sort((a, b) => b.value - a.value)

    const health = getProfitHealth(profitPerCow)

    return {
      calvesWeaned,
      cullCowCount,
      calfRevenue,
      cullRevenue,
      totalRevenue,
      revenuePerCow,
      expensePerHead,
      totalExpenses,
      netProfit,
      profitPerCow,
      breakEvenPrice,
      roi,
      expenses,
      health,
    }
  }, [herdSize, calvingRate, avgSaleWeight, marketPrice, cullCowPercent, cullCowPrice, feedCost, vetHealth, breedingAI, pastureLand, labor, mineralSupp, equipOverhead])

  const handleCalculate = () => {
    if (parseFloat(herdSize) > 0) setShowResults(true)
  }

  const handleReset = () => {
    applyPreset(OPERATION_TYPES[1])
    setShowResults(false)
  }

  const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 })
  const fmtDec = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const InputField = ({ label, icon, value, onChange, placeholder, hint, prefix, step }: { label: string; icon: React.ReactNode; value: string; onChange: (v: string) => void; placeholder: string; hint: string; prefix?: string; step?: string }) => (
    <div>
      <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
        {icon}
        {label}
      </label>
      <div className="relative">
        {prefix ? <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#606C38] font-medium text-sm">{prefix}</span> : null}
        <input
          type="number"
          value={value}
          onChange={(e) => { onChange(e.target.value); setShowResults(false) }}
          placeholder={placeholder}
          step={step || "1"}
          min="0"
          className={`w-full ${prefix ? "pl-8" : "px-4"} pr-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all placeholder:text-[#606C38]/40`}
        />
      </div>
      <p className="mt-1.5 text-xs text-[#606C38]">{hint}</p>
    </div>
  )

  return (
    <section id="cow-calf-profit-calculator" className="py-12 sm:py-16 lg:py-20 bg-[#FEFAE0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Ranch Profitability Tool
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            Cow Calf Profit Calculator
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-xl mx-auto">
            Estimate your cow-calf operation profitability by factoring in revenue from calf sales, cull cows, and all major expense categories.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-[#2D6A4F]/8 border border-[#B7E4C7]/40 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#D4A373] to-[#c4935f] px-6 sm:px-8 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#283618]" />
              </div>
              <div>
                <h3 className="text-[#283618] font-bold text-lg" style={{ fontFamily: "'Merriweather', serif" }}>
                  Cow Calf Profit Calculator
                </h3>
                <p className="text-[#283618]/70 text-sm">
                  Revenue, expenses &amp; profitability analysis
                </p>
              </div>
            </div>
          </div>

          {/* Input Section */}
          <div className="p-6 sm:p-8">
            {/* Operation Preset */}
            <div className="mb-6">
              <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                <Target className="w-3.5 h-3.5 text-[#40916C]" />
                Operation Type Preset
              </label>
              <div className="relative">
                <select
                  value={opType.name}
                  onChange={(e) => {
                    const op = OPERATION_TYPES.find((o) => o.name === e.target.value)
                    if (op) applyPreset(op)
                  }}
                  className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all appearance-none cursor-pointer pr-10"
                >
                  {OPERATION_TYPES.map((op) => (
                    <option key={op.name} value={op.name}>{op.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#606C38] pointer-events-none" />
              </div>
              <p className="mt-1.5 text-xs text-[#606C38]">{opType.description}</p>
            </div>

            {/* Revenue Inputs */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-[#2D6A4F] uppercase tracking-wider mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Revenue Inputs
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <InputField label="Breeding Herd Size" icon={<Scale className="w-3.5 h-3.5 text-[#40916C]" />} value={herdSize} onChange={setHerdSize} placeholder="e.g. 100" hint="Total number of breeding cows" />
                <InputField label="Calving Rate (%)" icon={<Percent className="w-3.5 h-3.5 text-[#40916C]" />} value={calvingRate} onChange={setCalvingRate} placeholder="e.g. 90" hint="Percent of cows that wean a calf" />
                <InputField label="Avg Calf Sale Weight (lbs)" icon={<Scale className="w-3.5 h-3.5 text-[#40916C]" />} value={avgSaleWeight} onChange={setAvgSaleWeight} placeholder="e.g. 575" hint="Average weaning weight at sale" />
                <InputField label="Market Price ($/lb)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={marketPrice} onChange={setMarketPrice} placeholder="e.g. 2.85" hint="Current feeder calf market price" prefix="$" step="0.01" />
                <InputField label="Cull Cow Rate (%)" icon={<Percent className="w-3.5 h-3.5 text-[#40916C]" />} value={cullCowPercent} onChange={setCullCowPercent} placeholder="e.g. 14" hint="Percent of herd culled annually" />
                <InputField label="Cull Cow Price ($/lb)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={cullCowPrice} onChange={setCullCowPrice} placeholder="e.g. 0.85" hint="Price per lb for cull cows (~1,150 lbs)" prefix="$" step="0.01" />
              </div>
            </div>

            {/* Expense Inputs */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-[#E76F51] uppercase tracking-wider mb-4 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                Annual Expenses (Per Cow)
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <InputField label="Feed & Hay ($/cow)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={feedCost} onChange={setFeedCost} placeholder="e.g. 600" hint="Hay, grain, stored feed per cow/year" prefix="$" />
                <InputField label="Vet & Health ($/cow)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={vetHealth} onChange={setVetHealth} placeholder="e.g. 40" hint="Vaccinations, deworming, vet visits" prefix="$" />
                <InputField label="Breeding / AI ($/cow)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={breedingAI} onChange={setBreedingAI} placeholder="e.g. 30" hint="Bull lease, AI costs, semen" prefix="$" />
                <InputField label="Pasture / Land ($/cow)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={pastureLand} onChange={setPastureLand} placeholder="e.g. 160" hint="Lease, property tax, fencing per head" prefix="$" />
                <InputField label="Labor ($/cow)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={labor} onChange={setLabor} placeholder="e.g. 100" hint="Hired labor or owner labor value" prefix="$" />
                <InputField label="Mineral / Supplement ($/cow)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={mineralSupp} onChange={setMineralSupp} placeholder="e.g. 38" hint="Mineral blocks, salt, supplements" prefix="$" />
                <div className="sm:col-span-2">
                  <InputField label="Equipment / Overhead ($/cow)" icon={<DollarSign className="w-3.5 h-3.5 text-[#40916C]" />} value={equipOverhead} onChange={setEquipOverhead} placeholder="e.g. 85" hint="Depreciation, fuel, insurance, utilities per head" prefix="$" />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCalculate}
                disabled={!herdSize || parseFloat(herdSize) <= 0}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2D6A4F] hover:bg-[#245a42] disabled:bg-[#B7E4C7] disabled:cursor-not-allowed text-[#FEFAE0] font-bold text-base sm:text-lg px-6 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:shadow-none disabled:translate-y-0"
              >
                <TrendingUp className="w-5 h-5" />
                Calculate Profit
              </button>
              {showResults ? (
                <button
                  onClick={handleReset}
                  className="inline-flex items-center justify-center gap-2 bg-[#FEFAE0] hover:bg-[#f0ead2] text-[#606C38] font-semibold px-5 py-4 rounded-xl border-2 border-[#B7E4C7] transition-all duration-200"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              ) : null}
            </div>
          </div>

          {/* Results Section */}
          {showResults && results ? (
            <div className="border-t-2 border-[#B7E4C7]/40 bg-gradient-to-b from-[#FEFAE0]/50 to-white p-6 sm:p-8 animate-fade-in-up">
              {/* Main Result */}
              <div className="text-center mb-6 pb-6 border-b border-[#B7E4C7]/30">
                <p className="text-sm font-semibold text-[#40916C] uppercase tracking-wider mb-1">
                  Net Profit
                </p>
                <p className={`text-4xl sm:text-5xl font-bold ${results.netProfit >= 0 ? "text-[#2D6A4F]" : "text-[#E76F51]"}`} style={{ fontFamily: "'Merriweather', serif" }}>
                  {fmt(results.netProfit)}
                </p>
                <p className="mt-2 text-sm text-[#606C38]">
                  {herdSize} cow operation &bull; {results.calvesWeaned} calves weaned &bull; {results.cullCowCount} cull cows
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-[#2D6A4F]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Total Revenue</span>
                  </div>
                  <p className="text-2xl font-bold text-[#2D6A4F]">{fmt(results.totalRevenue)}</p>
                  <p className="text-xs text-[#606C38] mt-1">{fmt(results.revenuePerCow)}/cow</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-[#E76F51]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Total Expenses</span>
                  </div>
                  <p className="text-2xl font-bold text-[#E76F51]">{fmt(results.totalExpenses)}</p>
                  <p className="text-xs text-[#606C38] mt-1">{fmt(results.expensePerHead)}/cow</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-[#D4A373]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Profit / Cow</span>
                  </div>
                  <p className={`text-2xl font-bold ${results.profitPerCow >= 0 ? "text-[#283618]" : "text-[#E76F51]"}`}>{fmt(results.profitPerCow)}</p>
                  <p className="text-xs text-[#606C38] mt-1">per breeding cow</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent className="w-4 h-4 text-[#606C38]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">ROI</span>
                  </div>
                  <p className={`text-2xl font-bold ${results.roi >= 0 ? "text-[#283618]" : "text-[#E76F51]"}`}>{results.roi.toFixed(1)}%</p>
                  <p className="text-xs text-[#606C38] mt-1">return on investment</p>
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="bg-[#FEFAE0] rounded-xl p-4 sm:p-5 border border-[#B7E4C7]/30 mb-4">
                <h4 className="font-bold text-sm text-[#283618] mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#2D6A4F]" />
                  Revenue Breakdown
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-[#606C38]">Calf Sales:</span>
                    <span className="ml-1 font-semibold text-[#283618]">{fmt(results.calfRevenue)}</span>
                    <span className="text-[#606C38] text-xs ml-1">({results.calvesWeaned} head x {avgSaleWeight} lbs x {fmtDec(parseFloat(marketPrice))}/lb)</span>
                  </div>
                  <div>
                    <span className="text-[#606C38]">Cull Cow Sales:</span>
                    <span className="ml-1 font-semibold text-[#283618]">{fmt(results.cullRevenue)}</span>
                    <span className="text-[#606C38] text-xs ml-1">({results.cullCowCount} head)</span>
                  </div>
                  <div>
                    <span className="text-[#606C38]">Break-Even Price:</span>
                    <span className="ml-1 font-semibold text-[#283618]">{fmtDec(results.breakEvenPrice)}/lb</span>
                  </div>
                </div>
              </div>

              {/* Expense Breakdown Bar */}
              <div className="bg-[#FEFAE0] rounded-xl p-4 sm:p-5 border border-[#B7E4C7]/30 mb-6">
                <h4 className="font-bold text-sm text-[#283618] mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#40916C]" />
                  Expense Breakdown (Per Cow)
                </h4>
                {/* Stacked bar */}
                <div className="h-6 rounded-full overflow-hidden flex mb-4">
                  {results.expenses.map((exp) => {
                    const pct = (exp.value / results.expensePerHead) * 100
                    return (
                      <div
                        key={exp.name}
                        className="h-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: exp.color }}
                        title={`${exp.name}: ${fmt(exp.value)} (${pct.toFixed(1)}%)`}
                      />
                    )
                  })}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {results.expenses.map((exp) => {
                    const pct = (exp.value / results.expensePerHead) * 100
                    return (
                      <div key={exp.name} className="flex items-center gap-2 text-xs">
                        <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: exp.color }} />
                        <span className="text-[#606C38]">{exp.name}:</span>
                        <span className="font-semibold text-[#283618]">{fmt(exp.value)} ({pct.toFixed(0)}%)</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Health Assessment */}
              <div
                className="rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-3 sm:gap-4 border"
                style={{ backgroundColor: results.health.bgColor, borderColor: results.health.color + "30" }}
              >
                <div className="shrink-0 mt-0.5">
                  {results.health.icon === "check" ? (
                    <CheckCircle className="w-5 h-5" style={{ color: results.health.color }} />
                  ) : results.health.icon === "warning" ? (
                    <AlertTriangle className="w-5 h-5" style={{ color: results.health.color }} />
                  ) : (
                    <AlertTriangle className="w-5 h-5" style={{ color: results.health.color }} />
                  )}
                </div>
                <div>
                  <p className="font-bold text-sm" style={{ color: results.health.color }}>
                    Profitability: {results.health.label}
                  </p>
                  <p className="text-sm text-[#283618]/80 mt-1 leading-relaxed">
                    {results.health.message}
                  </p>
                </div>
              </div>

              <p className="mt-5 text-xs text-[#606C38]/70 text-center leading-relaxed">
                These estimates are based on general industry averages and may vary with local market conditions, input costs, and management practices. Consult your local extension office or agricultural lender for personalized analysis.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
